import { DownOutlined, VerticalAlignTopOutlined } from "@ant-design/icons";
import {
  Button,
  Dropdown,
  Menu,
  Tooltip,
  message,
  Modal,
  notification,
  Segmented
} from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import ColumnConfigViews from "components/column-config-views";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import ImportMaterialLogs from "components/import-material-logs";
import { CiqIconBox } from "components/svg-icons";
import {
  EFeaturePageId,
  ErrorMessages,
  InfoMessages,
  ProjectPermissionEnum,
  SidepanelStyles,
  SuccessMessages
} from "constants/index";
import { AppContext } from "context/AppProvider";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import { exportEntityLogAsCSV } from "entity-app/utils/export-entity-log-as-csv";
import { resetGridFilters } from "entity-app/utils/utils";
import { useCallback, useContext, useMemo, useState } from "react";
import { newEntityNotificationMessage } from "entity-app/shared-components/new-entity-notification-message";
import { AgGridReact } from "ag-grid-react";
import { useHistory } from "react-router";
import FeatureBulkEditDurationsNewModel from "components/feature-bulk-edit-durations/feature-bulk-edit-durations-new-model";
import ErrorBoundary from "components/error-boundary";
import { FeatureTypes } from "entity-app/constants";
import BulkFeatureEditMaterialDetails from "components/bulk-edit-material/bulk-feature-edit-material-details";
import RequestDurationsMulti from "components/request-durations/request-durations-multi/request-durations-multi";
import { getGridStatusBudge } from "components/ciq-log-page-header/grid-status-budge";
import BulkFeatureEditMaterialStatus from "components/bulk-edit-material/bulk-feature-edit-material-status";
import BulkEditDeadlineDateModal from "components/bulk-edit-dateline-date/bulk-edit-dateline-date";
import { TMaterialLog } from "../model";
import CreateMaterialEntityComponent from "./create-material-entity";

type Prop = {
  selectedRows: TMaterialLog[] | null;
  gridRef: React.RefObject<AgGridReact<any>>;
  filterChipRef: React.RefObject<FilterChipComponentRef>;
  pageData: {
    entityList: TMaterialLog[] | undefined;
    fetchLogData: () => Promise<void>;
    materialHeaderMap: any;
    mutationUpdateFeaturesData: (payload: any) => Promise<any>;
  };
  featureKey: string;
  projectId: string;
  statsData:
    | {
        displayCount: number;
        filterName: undefined | string;
      }
    | undefined;
  pageSidebarState: {
    isSidebarOpen: boolean;
    sidebarAction: (params: {
      feature: string;
      action: "open" | "close";
      itemDetailsToSet: any | null;
    }) => void;
  };
};

export default function MaterialHeaderItems({
  selectedRows,
  gridRef,
  pageData,
  filterChipRef,
  statsData,
  featureKey,
  projectId,
  pageSidebarState = {
    isSidebarOpen: false,
    sidebarAction: () => {}
  }
}: Prop) {
  const { projectDetails, isFeatureFlagEnabled, tokenContents } =
    useContext(ProjectContext);
  const { materialListFilter, setMaterialListFilter } = useContext(AppContext);

  const history = useHistory();
  const [bulkEditDialog, onMaterialEditClick] = useState<
    | "details"
    | "duration"
    | "request_durations_from_sc"
    | null
    | "status"
    | "deadline_date"
  >(null);
  const [showImportLogDrawer, setShowImportLogDrawer] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  const [api, contextHolder] = notification.useNotification();
  const openNotification = (id: string, title: string) => {
    api.open(
      newEntityNotificationMessage({
        projectId,
        id,
        title,
        featureKey
      })
    );
  };

  const isMaterialExtractionEnabled = isFeatureFlagEnabled(
    "MATERIAL_EXTRACTION"
  );

  const isRequestLeadTimeFeatureEnabled =
    isFeatureFlagEnabled("REQUEST_LEAD_TIME");

  const isMaterialBulkLinkingEnabled = isFeatureFlagEnabled(
    "MATERIAL_MULTIPLE_LINKING"
  );

  const cannotCreateMaterial: any = isPermissionNotGrantted(
    ProjectPermissionEnum.CreateMaterial,
    tokenContents?.role!
  );

  const cannotRequestLeadTime: any = isPermissionNotGrantted(
    ProjectPermissionEnum.RequestLeadTime,
    tokenContents?.role!
  );

  const onAllMaterialClick = useCallback(() => {
    resetGridFilters({
      setListFilter: setMaterialListFilter,
      api: gridRef.current?.api
    });
  }, [gridRef, setMaterialListFilter]);

  const bulkEditActionMenuItems = useMemo(() => {
    return (
      <Menu>
        {/* Group Title */}
        <Menu.ItemGroup key="bulk-edit" title="Bulk Edit">
          <Menu.Item
            key="details"
            onClick={() => onMaterialEditClick("details")}
          >
            Details
          </Menu.Item>

          <Menu.Item
            key="deadline_date"
            onClick={() => onMaterialEditClick("deadline_date")}
          >
            {pageData?.materialHeaderMap?.manual_entry_date || "Deadline Date"}
          </Menu.Item>

          <Menu.Item
            key="durations"
            onClick={() => onMaterialEditClick("duration")}
          >
            Durations
          </Menu.Item>

          {isMaterialBulkLinkingEnabled && (
            <Menu.Item
              key="links"
              onClick={() =>
                history.push(
                  `/project/${projectDetails?.id}/schedule/material-linking`,
                  {
                    name: "Bulk Edit Links",
                    selectedRows: selectedRows?.map((x) => ({
                      ...x,
                      name: x.title
                    }))
                  }
                )
              }
            >
              Links
            </Menu.Item>
          )}

          <Menu.Item key="status" onClick={() => onMaterialEditClick("status")}>
            Status
          </Menu.Item>
        </Menu.ItemGroup>

        {/* Divider */}
        <Menu.Divider />

        {/* Footer Action */}
        {isRequestLeadTimeFeatureEnabled && !cannotRequestLeadTime && (
          <Menu.Item
            key="request_lead_times"
            onClick={() => onMaterialEditClick("request_durations_from_sc")}
          >
            Request Lead Times
          </Menu.Item>
        )}
      </Menu>
    );
  }, [
    history,
    projectDetails?.id,
    selectedRows,
    isMaterialBulkLinkingEnabled,
    isRequestLeadTimeFeatureEnabled,
    cannotRequestLeadTime,
    pageData?.materialHeaderMap?.manual_entry_date
  ]);

  const deleteMaterials = useCallback(() => {
    Modal.confirm({
      title: InfoMessages.deleteMaterialConfirmTitle,
      okText: "Yes",
      cancelText: "No",
      async onOk() {
        try {
          if (!selectedRows) return;
          const payload = {
            variables: {
              feature_instance_ids: selectedRows.map((s: any) => s.id),
              feature_type_id: 2
            }
          };
          const deleteMaterialsRes = await pageData.mutationUpdateFeaturesData(
            payload
          );
          if (deleteMaterialsRes.data) {
            message.success(SuccessMessages.deleteSuccess);
            pageData.fetchLogData();
          }
          if (deleteMaterialsRes.errors) {
            console.error(deleteMaterialsRes.errors);
            message.error(ErrorMessages.deleteMaterialFailed);
          }
        } catch (ex: any) {
          console.log("exception in delete material ", ex);
          const errorMsg = ex.message || ErrorMessages.deleteMaterialFailed;
          message.error(errorMsg);
        }
      }
    });
  }, [pageData, selectedRows]);

  const bulkEditButton = useMemo(() => {
    if (!selectedRows) return null;
    const selectedCount = selectedRows.length;
    if (selectedCount < 1) return null;
    return (
      <div
        className="bg-ciq-gray-500 bg-opacity-70 py-1 px-4 flex items-center space-x-2 rounded-md"
        key="MaterialLogPage_BulkEdit"
      >
        <div className="py-1 whitespace-nowrap" key="MaterialLogPage_Selected">
          {selectedRows?.length} Selected
        </div>
        {selectedCount > 1 && (
          <Dropdown
            overlay={bulkEditActionMenuItems}
            key="MaterialLogPage_bulkEdit"
            disabled={cannotCreateMaterial}
          >
            <Button
              disabled={cannotCreateMaterial}
              title={
                cannotCreateMaterial
                  ? ErrorMessages.PermissionNotGranted
                  : "Actions"
              }
            >
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        )}
        <Button
          key="MaterialLogPage_Delete"
          onClick={deleteMaterials}
          size="middle"
          className="bottomBarBtn"
          title={
            cannotCreateMaterial ? ErrorMessages.PermissionNotGranted : "Delete"
          }
          disabled={cannotCreateMaterial}
        >
          Delete
        </Button>
      </div>
    );
  }, [
    bulkEditActionMenuItems,
    cannotCreateMaterial,
    deleteMaterials,
    selectedRows
  ]);

  const headerItems = useMemo(() => {
    const menus = [] as JSX.Element[];
    if (!materialListFilter)
      menus.push(
        <FilterChipComponent
          key="MaterialLogPage_FilterChip"
          gridRef={gridRef}
          ref={filterChipRef}
          data-testid="material-header-filter-chip"
        />
      );
    if (bulkEditButton && !pageSidebarState.isSidebarOpen)
      menus.push(bulkEditButton);
    menus.push(
      <CiqAgSearch
        key="MaterialLogPage_search"
        gridRef={gridRef}
        placeholder="Search Materials"
        data-testid="material-header-search"
      />
    );

    menus.push(
      <ColumnConfigViews
        key="MaterialLogPage_ColumnConfigViews"
        featureId={EFeaturePageId.MATERIAL}
        setFilterStateFromData={(columnStates) => {
          if (gridRef.current)
            UtilColumnConfig.setFilterStateFromData({
              columnStates,
              gridRef: {
                api: gridRef.current!.api
              }
            });
        }}
        getColumnStateFromGrid={() => {
          if (gridRef.current) {
            return UtilColumnConfig.getColumnStateFromGrid({
              gridRef: {
                api: gridRef.current!.api
              }
            });
          }
          return null;
        }}
        // afterApplyFilterCallBack={pageData.calculateMaxNumberOfMilestone}
        data-testid="material-header-column-config"
      />
    );

    if (pageSidebarState.isSidebarOpen) {
      menus.push(
        <Tooltip
          title="More Actions available"
          placement="topRight"
          key="close_sidepanel_tooltip"
        >
          <Button
            key="close_sidepanel"
            data-testid="close-sidepanel-button"
            onClick={() => {
              pageSidebarState.sidebarAction({
                feature: "",
                action: "close",
                itemDetailsToSet: null
              });
            }}
          >
            <VerticalAlignTopOutlined rotate={90} />
          </Button>
        </Tooltip>
      );
      return menus;
    }

    menus.push(
      <Button
        key="MaterialLogPage_importLog"
        onClick={() => {
          setShowImportLogDrawer(true);
        }}
        disabled={cannotCreateMaterial}
        title={cannotCreateMaterial ? ErrorMessages.PermissionNotGranted : ""}
        data-testid="material-header-import-log"
      >
        Import Log
      </Button>
    );

    if (isMaterialExtractionEnabled) {
      menus.push(
        <Button
          key="MaterialLogPage_extraction"
          onClick={() => {
            history.replace(
              `/project/${projectDetails?.id}/materials/extracted`,
              {
                isOpenModel: true
              }
            );
          }}
          data-testid="material-header-extraction"
        >
          Extract from Drawings
        </Button>
      );
    }

    menus.push(
      <Button
        key="MaterialLogPage_export"
        placeholder="Export Material Log"
        onClick={() => {
          if (projectDetails && gridRef.current)
            exportEntityLogAsCSV({
              entityName: "Materials_log_",
              projectDetails,
              gridRef: {
                api: gridRef.current!.api
              },
              includedColumns: ["description"],
              excludedColumns: ["_milestones", "_durations", "action"],
              sortPosition: [
                {
                  colId: "released_early_msg",
                  dependentColId: "material_released_early",
                  position: "after"
                }
              ]
            });
        }}
        disabled={!pageData.entityList}
        data-testid="material-header-export"
      >
        Export
      </Button>
    );

    menus.push(
      <Button
        key="MaterialLogPage_new"
        disabled={cannotCreateMaterial}
        title={
          cannotCreateMaterial
            ? ErrorMessages.PermissionNotGranted
            : "Create New Material"
        }
        onClick={() => {
          setShowDrawer(!showDrawer);
        }}
        type="primary"
        data-testid="material-header-new-material"
      >
        New Material
      </Button>
    );

    return menus;
  }, [
    materialListFilter,
    gridRef,
    filterChipRef,
    bulkEditButton,
    pageSidebarState,
    cannotCreateMaterial,
    history,
    isMaterialExtractionEnabled,
    pageData.entityList,
    projectDetails,
    showDrawer
  ]);

  const entityName = (
    <div className="flex items-center justify-start">
      <CiqIconBox feature="MATERIALS" size={20} padding={8} />
      <div className="flex flex-col pl-3">
        <div className="text-black text-xl font-semibold">Materials</div>
      </div>
    </div>
  );

  const statusBudge = useMemo(
    () =>
      getGridStatusBudge(
        {
          title: "Materials",
          totalCount: pageData.entityList?.length,
          filterStats: statsData
        },
        onAllMaterialClick
      ),
    [onAllMaterialClick, pageData.entityList?.length, statsData]
  );

  return (
    <div
      className="w-full px-6 py-4 flex justify-between items-center"
      data-testid="material-header-items-container"
    >
      <div className="flex space-x-2 items-center text-black">
        <div
          className="text-xl font-semibold"
          data-testid="material-header-title"
        >
          {entityName}
        </div>
        <div className="flex shrink-0" data-testid="material-header-stats">
          {statusBudge}
        </div>
        {isMaterialExtractionEnabled && !pageSidebarState.isSidebarOpen && (
          <Segmented
            options={["Log", "Extracted"]}
            value="Log"
            data-testid="material-header-segmented"
            onChange={(e) => {
              if (e === "Extracted")
                history.replace(`/project/${projectId}/materials/extracted`, {
                  isOpenModel: false
                });
              if (e === "Log")
                history.replace(`/project/${projectId}/materials`);
            }}
          />
        )}
      </div>

      <div
        className="flex space-x-2 items-center"
        data-testid="material-header-actions"
      >
        {headerItems}
      </div>

      {contextHolder}

      {showDrawer && (
        <CreateMaterialEntityComponent
          setDrawerOpen={setShowDrawer}
          showDrawerOpen={showDrawer}
          modelTitle="Create Material"
          onCreated={(title: string, id: string) => {
            setShowDrawer(false);
            openNotification(id, title);
            pageData.fetchLogData();
          }}
        />
      )}

      {showImportLogDrawer && (
        <ImportMaterialLogs
          materialHeaderMap={pageData.materialHeaderMap}
          existingMaterialsCount={pageData.entityList?.length || 0}
          showImportLogDrawer={showImportLogDrawer}
          onCancel={() => {
            setShowImportLogDrawer(false);
          }}
          onComplete={() => {
            pageData.fetchLogData();
          }}
        />
      )}

      <Modal
        title="Bulk Edit Durations"
        className="custom-drawer with-padding-fix"
        style={SidepanelStyles.style}
        width="calc(100vw - 200px)"
        footer={[
          <div className="flex w-full space-x-3 justify-end" key={100}>
            <Button
              onClick={() => {
                onMaterialEditClick(null);
                gridRef?.current?.api?.deselectAll();
              }}
              key={101}
            >
              Close
            </Button>
          </div>
        ]}
        open={bulkEditDialog === "duration"}
        onCancel={() => {
          onMaterialEditClick(null);
        }}
        destroyOnClose
        keyboard={false}
      >
        <ErrorBoundary>
          <FeatureBulkEditDurationsNewModel
            selectedRows={selectedRows!}
            featureTypeId={FeatureTypes.MATERIALS}
            onUpdateDone={() => {
              pageData.fetchLogData();
            }}
          />
        </ErrorBoundary>
      </Modal>

      {selectedRows && bulkEditDialog === "details" && (
        <BulkFeatureEditMaterialDetails
          onCancelClick={() => {
            onMaterialEditClick(null);
          }}
          onUpdateDone={() => {
            onMaterialEditClick(null);
            gridRef?.current?.api?.deselectAll();
            pageData.fetchLogData();
          }}
          setShowEdit={onMaterialEditClick}
          showEdit={bulkEditDialog === "details"}
          selectedRows={selectedRows}
        />
      )}

      {selectedRows && bulkEditDialog === "status" && (
        <BulkFeatureEditMaterialStatus
          onCancelClick={() => {
            onMaterialEditClick(null);
          }}
          onUpdateDone={() => {
            onMaterialEditClick(null);
            gridRef?.current?.api?.deselectAll();
            pageData.fetchLogData();
          }}
          setShowEdit={onMaterialEditClick}
          showEdit={bulkEditDialog === "status"}
          selectedRows={selectedRows}
        />
      )}

      {selectedRows && bulkEditDialog === "deadline_date" && (
        <BulkEditDeadlineDateModal
          onUpdateDone={() => {
            onMaterialEditClick(null);
            gridRef?.current?.api?.deselectAll();
            pageData.fetchLogData();
          }}
          setShowEdit={onMaterialEditClick}
          showEdit={bulkEditDialog === "deadline_date"}
          selectedRows={selectedRows}
          columnHeaderMap={pageData.materialHeaderMap}
          featureTypeId={FeatureTypes.MATERIALS}
        />
      )}

      <Modal
        title={
          <div>
            <div className="font-semibold text-sm">Request Lead Times</div>
            <div className="text-xs mt-0.5">
              This will send emails to the Assigned Subcontractors with a link
              to enter Lead Times.
            </div>
          </div>
        }
        open={bulkEditDialog === "request_durations_from_sc"}
        bodyStyle={{ padding: 0 }}
        width={1000}
        footer={null}
        onCancel={() => {
          onMaterialEditClick(null);
        }}
        destroyOnClose
        centered
        className="request-durations-modal"
      >
        <div className="h-[500px] overflow-y-hidden">
          {selectedRows && selectedRows.length > 0 && (
            <RequestDurationsMulti
              selectedItems={selectedRows}
              onCancel={() => {
                onMaterialEditClick(null);
              }}
              onComplete={() => {
                onMaterialEditClick(null);
                gridRef.current?.api?.deselectAll();
              }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
